import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import HistoryLog from '../HistoryLog';
import { EVENT_TIMELINE_ENTITY_TYPES, EVENT_TIMELINE_SUB_TYPES } from 'App/babel/enum/timeline';
import ContactTimelineCreateContact from './ContactTimelineCreateContact';
import { getTimelineRow } from '../../../babel/components/Helpers/getTimelineRow';
import T from 'Components/Helpers/translate';
import ComparisonTypes from 'Resources/ComparisonTypes';
import Contact from 'App/resources/Model/Contact';
import usePartialLoader, { type FetcherFunction } from 'App/components/hooks/usePartialLoader';
import type { HistoryLogRenderedBy } from 'App/components/HistoryLog/ModalHistoryLog';
import { TimelineComponentProps } from 'Components/TimelineRow/TimelineGeneric/TimelineCardConfigs';
import TimelineLoadMore from 'Components/TimelineRow/TimelineLoadMore/TimelineLoadMore';
import Event from 'App/resources/Model/Event';
import HistoryLogSearch from '../HistoryLogSearch';
import { expandHistoryLogSearch } from '../historyLogSearchSynonyms';
import HistoryLogFilterButtons from '../HistoryLogFilterButtons';
import RequestBuilder from 'Resources/RequestBuilder';
import { makeCancelable } from 'App/babel/helpers/promise';

const listeners = ['comment.added', 'comment.deleted', 'comment.updated'];

// Mirrors the client card's taxonomy, minus the types that only exist at company
// level (signals, news). Zero-count filters are dropped by
// HistoryLogFilterButtons, so a contact only ever shows the ones it has.
const historyTypes = ['all', 'activities', 'appointments', 'order', 'mail', 'opportunities', 'market'] as const;
type HistoryType = (typeof historyTypes)[number];
type HistoryTypeCounts = { [k in HistoryType]?: number };

const filterEntityTypes: Record<Exclude<HistoryType, 'all' | 'market'>, string[]> = {
	activities: ['Activity'],
	appointments: ['Appointment'],
	order: ['Order', 'Agreement'],
	mail: ['Mail'],
	opportunities: ['Opportunity']
};

type Props = {
	contact: Contact;
	client: Contact['client'];
	notesFilterActive?: boolean;
	renderedBy: HistoryLogRenderedBy;
	selectButtons?: ReactNode;
	cardAdditionalProps?: Partial<TimelineComponentProps>;
};

const fetcher: FetcherFunction<
	Pick<Props, 'notesFilterActive' | 'contact'> & {
		search?: string;
		historyType?: HistoryType;
		onlyMetadata?: boolean;
	},
	Event
> = (filters, { contact, notesFilterActive, search, historyType = 'all', onlyMetadata = false }) => {
	const event = Tools.Event;

	filters.addFilter(event.attr.contacts.attr?.id, ComparisonTypes.Equals, contact.id);
	filters.addFilter(
		event.attr.date,
		ComparisonTypes.LessThanEquals,
		moment.tz(Tools.userTimezone ?? 'Europe/Stockholm')
	);
	// Marketing events hang off their own feed pair on the API, the same one the
	// Marketing tab reads, so they need the feed swapped rather than an entityType.
	if (historyType === 'market') {
		filters.addFilter({ field: 'feedContactMarket' }, ComparisonTypes.Equals, null);
	} else {
		filters.addFilter({ field: 'feedContact2' }, ComparisonTypes.Equals, null);
		filters.addFilter(event.attr.entityType, ComparisonTypes.NotEquals, ['Contact']);
		if (historyType in filterEntityTypes) {
			filters.addFilter(
				event.attr.entityType,
				ComparisonTypes.Equals,
				filterEntityTypes[historyType as keyof typeof filterEntityTypes]
			);
		}
	}

	if (notesFilterActive) {
		filters.addFilter({ field: 'feedNotes' }, ComparisonTypes.Equals, null);
	}

	if (search) {
		filters.addFilter(event.attr.search, ComparisonTypes.Equals, expandHistoryLogSearch(search));
	}

	filters.addSort(event.attr.date, false);
	filters.addSort(event.attr.entityType, false);

	if (onlyMetadata) {
		filters.limit = 0;
	}

	return event.find(filters.build());
};

const ContactHistoryLog = ({ contact, client, renderedBy, notesFilterActive, selectButtons }: Props) => {
	const [search, setSearch] = useState('');
	const [historyType, setHistoryType] = useState<HistoryType>('all');
	const [metadata, setMetadata] = useState<HistoryTypeCounts>({ all: 0 });
	const containerRef = useRef<HTMLDivElement>(null);
	const onSearchChange = useCallback((value: string) => setSearch(value), []);

	const {
		data: events,
		total: totalEvents,
		loading,
		loadMore,
		reset
	} = usePartialLoader({
		fetcher,
		broadcastTypes: listeners,
		fetcherProps: {
			contact,
			notesFilterActive,
			search,
			historyType
		}
	});

	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		reset();
	}, [search, historyType]);

	// One count per filter, so the buttons can show them and hide themselves when
	// the contact has nothing of that kind.
	useEffect(() => {
		const promise = makeCancelable(
			Promise.all(
				historyTypes.map(type =>
					fetcher(new RequestBuilder(), {
						contact,
						notesFilterActive,
						historyType: type,
						onlyMetadata: true
					}).then(res => ({ type, count: res.metadata.total }))
				)
			).then(counts => {
				setMetadata(counts.reduce((acc, cur) => ({ ...acc, [cur.type]: cur.count }), {} as HistoryTypeCounts));
			})
		);

		return () => promise.cancel();
	}, [contact.id, notesFilterActive]);

	// Latch search visibility so clearing the query doesn't unmount the input
	// (which would steal focus) while the timeline refetches.
	const hasShownSearchRef = useRef(false);
	if (totalEvents > 0 || search) {
		hasShownSearchRef.current = true;
	}
	const showSearchInput = hasShownSearchRef.current;

	if (!contact || !contact.id) {
		return null;
	}
	let timelineElements: ReactNode[] = [];
	if (events && events.length) {
		timelineElements = events.map(event => {
			return event ? (
				<div key={event.id}>{getTimelineRow(event, 'Contact', notesFilterActive, false, renderedBy)}</div>
			) : null;
		});
	}

	const contactRegEvent = {
		entityType: EVENT_TIMELINE_ENTITY_TYPES.CONTACT,
		subType: EVENT_TIMELINE_SUB_TYPES.CREATED,
		users: contact.regBy ? [contact.regBy] : [],
		entityId: contact.id,
		client: client,
		contact: contact
	};

	const filterButtons = [
		{ title: T('all'), value: 'all' as const },
		{ title: T('default.activities'), value: 'activities' as const, icon: 'activity' as const },
		{ title: T('default.appointments'), value: 'appointments' as const, icon: 'calendar' as const },
		{ title: T('default.order'), value: 'order' as const, icon: 'dollar' as const },
		{ title: T('default.mail'), value: 'mail' as const, icon: 'envelope' as const },
		{ title: T('default.opportunities'), value: 'opportunities' as const, icon: 'opportunity' as const },
		{ title: T('default.market'), value: 'market' as const, icon: 'bullhorn' as const }
	];

	return (
		<div ref={containerRef}>
			<HistoryLog
				title={T('history')}
				selectButtons={
					<>
						{selectButtons}
						<HistoryLogFilterButtons
							containerRef={containerRef}
							buttons={filterButtons}
							historyType={historyType}
							setHistoryType={setHistoryType}
							metadata={metadata}
						/>
					</>
				}
				searchInput={showSearchInput ? <HistoryLogSearch onSearchChange={onSearchChange} /> : null}
				loading={loading}
			>
				{timelineElements}
				{timelineElements.length < totalEvents ? (
					<TimelineLoadMore
						newEventsLoading={loading}
						numEventsLoaded={events.length}
						totalEvents={totalEvents ? totalEvents : null}
						incrementOffset={loadMore}
					/>
				) : null}
				<ContactTimelineCreateContact isHistoryLog event={contactRegEvent} />
			</HistoryLog>
		</div>
	);
};

export default ContactHistoryLog;
